/**
 * Client-side color extraction from an uploaded logo.
 *
 * Pipeline:
 *   1. Load image via <img> (supports PNG, JPG, WebP, SVG).
 *   2. Draw to 64×64 <canvas>, read ImageData.
 *   3. Run median-cut quantization → top N dominant colors.
 *   4. Filter near-white / near-black / near-gray backgrounds.
 *   5. Rank remaining by saturation × frequency → primary.
 *   6. Second rank → accent.
 *   7. Derive text/background/border/status from HSL ramp.
 *
 * Returns a full palette suitable for the BrandKit.colors slot. The admin
 * can tweak anything before saving.
 *
 * Pure browser JS — no dependencies beyond the DOM.
 */

export interface ExtractedPalette {
  primary: string;
  primaryContrast: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
  success: string;
  warning: string;
  danger: string;
  /** The ranked top candidates, in case the admin wants to swap them in/out. */
  candidates: string[];
  /** Rough confidence score (0-1). Low = muddy logo (e.g. photograph). */
  confidence: number;
}

type RGB = [number, number, number];

// ─────────────────────────────────────────────────────────────────────────────
// Public entry point
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract a palette from a logo File or URL.
 * Throws on decode failure.
 */
export async function extractPaletteFromLogo(
  source: File | string,
  opts: { size?: number; candidateCount?: number } = {}
): Promise<ExtractedPalette> {
  const size = opts.size ?? 64;
  const candidateCount = opts.candidateCount ?? 8;

  const imageData = await decodeToImageData(source, size);
  const pixels = imageDataToRgbArray(imageData);
  const quantized = medianCut(pixels, candidateCount);

  // Rank candidates: saturation × frequency, filtering trivially bad ones.
  const ranked = quantized
    .map((q) => ({
      rgb: q.rgb,
      count: q.count,
      saturation: saturationOf(q.rgb),
      lightness: lightnessOf(q.rgb),
      hex: rgbToHex(q.rgb),
    }))
    .sort((a, b) => {
      const aScore = a.saturation * a.count;
      const bScore = b.saturation * b.count;
      return bScore - aScore;
    });

  // Discard near-white/near-black/near-gray — those are likely bg, not brand.
  const expressive = ranked.filter(
    (c) => c.saturation > 0.15 && c.lightness > 0.08 && c.lightness < 0.92
  );

  // Fallback: if every color was filtered out (e.g. monochrome logo), keep top-by-count.
  const pool = expressive.length > 0 ? expressive : ranked;

  const primary = pool[0] ?? ranked[0];
  const accent = pool[1] ?? pool[0] ?? primary;

  // Confidence: high if primary sits far away from pure white/gray.
  const confidence = Math.min(1, primary.saturation * 2);

  // Derive light/dark mode appropriate scaffolding from the primary hue.
  const derived = deriveFromPrimary(primary.rgb);

  return {
    primary: primary.hex,
    primaryContrast: bestContrastAgainst(primary.rgb),
    accent: accent.hex,
    background: derived.background,
    surface: derived.surface,
    text: derived.text,
    textMuted: derived.textMuted,
    border: derived.border,
    success: '#16a34a',
    warning: '#d97706',
    danger: '#dc2626',
    candidates: pool.slice(0, 6).map((c) => c.hex),
    confidence,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Decoding
// ─────────────────────────────────────────────────────────────────────────────

async function decodeToImageData(source: File | string, size: number): Promise<ImageData> {
  const url = typeof source === 'string' ? source : URL.createObjectURL(source);

  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2D canvas context unavailable');
    // White background — matters for PNGs with transparency so we don't
    // quantize transparent pixels as black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    // Preserve aspect ratio by fitting inside the square.
    const { drawX, drawY, drawW, drawH } = fitInside(img.naturalWidth, img.naturalHeight, size);
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    return ctx.getImageData(0, 0, size, size);
  } finally {
    if (typeof source !== 'string') URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image — check CORS / format'));
    img.src = url;
  });
}

function fitInside(w: number, h: number, size: number) {
  const ratio = Math.min(size / w, size / h);
  const drawW = Math.max(1, Math.round(w * ratio));
  const drawH = Math.max(1, Math.round(h * ratio));
  return {
    drawW,
    drawH,
    drawX: Math.round((size - drawW) / 2),
    drawY: Math.round((size - drawH) / 2),
  };
}

function imageDataToRgbArray(data: ImageData): RGB[] {
  const pixels: RGB[] = [];
  const a = data.data;
  for (let i = 0; i < a.length; i += 4) {
    // Skip fully transparent pixels.
    if (a[i + 3] < 32) continue;
    pixels.push([a[i], a[i + 1], a[i + 2]]);
  }
  return pixels;
}

// ─────────────────────────────────────────────────────────────────────────────
// Median-cut quantization
// ─────────────────────────────────────────────────────────────────────────────

interface Bucket {
  pixels: RGB[];
  minR: number;
  maxR: number;
  minG: number;
  maxG: number;
  minB: number;
  maxB: number;
}

function boundsOf(pixels: RGB[]): Bucket {
  let minR = 255,
    maxR = 0,
    minG = 255,
    maxG = 0,
    minB = 255,
    maxB = 0;
  for (const [r, g, b] of pixels) {
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
    if (g < minG) minG = g;
    if (g > maxG) maxG = g;
    if (b < minB) minB = b;
    if (b > maxB) maxB = b;
  }
  return { pixels, minR, maxR, minG, maxG, minB, maxB };
}

function longestDim(b: Bucket): 0 | 1 | 2 {
  const rng = [b.maxR - b.minR, b.maxG - b.minG, b.maxB - b.minB] as const;
  if (rng[0] >= rng[1] && rng[0] >= rng[2]) return 0;
  if (rng[1] >= rng[2]) return 1;
  return 2;
}

function splitBucket(b: Bucket): [Bucket, Bucket] {
  const dim = longestDim(b);
  const sorted = [...b.pixels].sort((p, q) => p[dim] - q[dim]);
  const mid = sorted.length >> 1;
  return [boundsOf(sorted.slice(0, mid)), boundsOf(sorted.slice(mid))];
}

function averageColor(pixels: RGB[]): RGB {
  let r = 0,
    g = 0,
    b = 0;
  for (const [pr, pg, pb] of pixels) {
    r += pr;
    g += pg;
    b += pb;
  }
  const n = pixels.length;
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

function medianCut(pixels: RGB[], count: number): { rgb: RGB; count: number }[] {
  if (pixels.length === 0) return [];
  let buckets: Bucket[] = [boundsOf(pixels)];
  // Split the biggest bucket repeatedly until we have `count` buckets.
  while (buckets.length < count) {
    // Pick the bucket with the biggest single-channel range.
    let biggest = 0;
    let biggestRange = 0;
    for (let i = 0; i < buckets.length; i++) {
      const b = buckets[i];
      const range = Math.max(b.maxR - b.minR, b.maxG - b.minG, b.maxB - b.minB);
      if (range > biggestRange && b.pixels.length > 1) {
        biggestRange = range;
        biggest = i;
      }
    }
    if (biggestRange === 0) break;
    const [a, c] = splitBucket(buckets[biggest]);
    buckets = [...buckets.slice(0, biggest), a, c, ...buckets.slice(biggest + 1)];
  }
  return buckets
    .map((b) => ({ rgb: averageColor(b.pixels), count: b.pixels.length }))
    .sort((a, b) => b.count - a.count);
}

// ─────────────────────────────────────────────────────────────────────────────
// Color math helpers
// ─────────────────────────────────────────────────────────────────────────────

function rgbToHex([r, g, b]: RGB): string {
  const h = (n: number) => n.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function hexToRgb(hex: string): RGB {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function saturationOf([r, g, b]: RGB): number {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  if (max === 0) return 0;
  return (max - min) / max;
}

function lightnessOf([r, g, b]: RGB): number {
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2 / 255;
}

function luminanceOf([r, g, b]: RGB): number {
  const norm = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

/** Returns #000 or #fff, whichever has the highest WCAG contrast against rgb. */
function bestContrastAgainst(rgb: RGB): string {
  const lum = luminanceOf(rgb);
  // Contrast with white: (1 + 0.05) / (lum + 0.05)
  const vsWhite = 1.05 / (lum + 0.05);
  const vsBlack = (lum + 0.05) / 0.05;
  return vsWhite >= vsBlack ? '#ffffff' : '#0a0a0a';
}

/**
 * Given a primary color, derive the surrounding neutral palette (bg, surface,
 * text, muted text, border). We pick a light scheme by default — most
 * storefronts are light. Admin can override.
 */
function deriveFromPrimary(_primary: RGB) {
  // Keep surroundings near-neutral but very slightly tinted toward primary
  // hue — adds cohesion without looking gimmicky.
  return {
    background: '#ffffff',
    surface: '#f8f9fb',
    text: '#0a0a0a',
    textMuted: '#525867',
    border: '#e5e7eb',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Exposed utilities (for BrandKit admin UI)
// ─────────────────────────────────────────────────────────────────────────────

/** WCAG 2.1 contrast ratio between two hex colors. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = luminanceOf(hexToRgb(hexA));
  const b = luminanceOf(hexToRgb(hexB));
  const [light, dark] = a > b ? [a, b] : [b, a];
  return (light + 0.05) / (dark + 0.05);
}

/** True if the pair passes WCAG AA for normal body text (≥ 4.5:1). */
export function meetsWcagAA(fg: string, bg: string): boolean {
  return contrastRatio(fg, bg) >= 4.5;
}
